import React, { useState } from 'react';
import { Users, Activity, Calendar, FileText, Bell, Coffee, Home } from 'lucide-react';
import CaregiverDashboard from './caregiver/CaregiverDashboard';
import ResidentMonitor from './caregiver/ResidentMonitor';
import CheckInScheduler from './caregiver/CheckInScheduler';
import ActivityReports from './caregiver/ActivityReports';
import AlertConfiguration from './caregiver/AlertConfiguration';

// Mock data for caregivers
const caregivers = [
  { id: '1', name: 'Dr. Sarah Johnson', role: 'Primary Physician', lastAccess: new Date() },
  { id: '2', name: 'Michael Chen', role: 'Family Member', lastAccess: new Date(Date.now() - 86400000) },
  { id: '3', name: 'Lisa Rodriguez', role: 'Home Nurse', lastAccess: new Date(Date.now() - 3600000) },
  { id: '4', name: 'Robert Wilson', role: 'Physical Therapist', lastAccess: new Date(Date.now() - 172800000) }
];

// Mock data for resident
const resident = {
  name: 'Emma Martinez',
  age: 78,
  address: '123 Maple Street, Apt 204',
  emergencyContact: 'Michael Chen (Son) - (555) 123-4567',
  careLevel: 'Moderate Assistance',
  primaryConditions: ['Arthritis', 'Early-stage dementia', 'Hypertension'],
};

const CaregiverPortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loggedInUser, setLoggedInUser] = useState(caregivers[0]);

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <CaregiverDashboard resident={resident} loggedInUser={loggedInUser} />;
      case 'monitor':
        return <ResidentMonitor resident={resident} />;
      case 'checkin':
        return <CheckInScheduler resident={resident} caregivers={caregivers} />;
      case 'reports':
        return <ActivityReports resident={resident} />;
      case 'alerts':
        return <AlertConfiguration resident={resident} />;
      default:
        return <CaregiverDashboard resident={resident} loggedInUser={loggedInUser} />;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Caregiver Coordination Portal</h2>
        <p className="text-gray-600">Manage care and monitor activity for {resident.name}</p>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Side Navigation */}
        <div className="w-full lg:w-64 mb-6 lg:mb-0 lg:mr-6">
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center space-x-3 mb-4 pb-4 border-b">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                {loggedInUser.name.charAt(0)}
              </div>
              <div>
                <div className="font-medium">{loggedInUser.name}</div>
                <div className="text-sm text-gray-500">{loggedInUser.role}</div>
              </div>
            </div>

            <nav className="space-y-1">
              <button
                className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-3 ${
                  activeTab === 'dashboard' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleTabChange('dashboard')}
              >
                <Home size={20} />
                <span>Dashboard</span>
              </button>
              <button
                className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-3 ${
                  activeTab === 'monitor' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleTabChange('monitor')}
              >
                <Activity size={20} />
                <span>Activity Monitor</span>
              </button>
              <button
                className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-3 ${
                  activeTab === 'checkin' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleTabChange('checkin')}
              >
                <Calendar size={20} />
                <span>Check-in Scheduler</span>
              </button>
              <button
                className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-3 ${
                  activeTab === 'reports' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleTabChange('reports')}
              >
                <FileText size={20} />
                <span>Activity Reports</span>
              </button>
              <button
                className={`w-full text-left px-4 py-2 rounded-md flex items-center space-x-3 ${
                  activeTab === 'alerts' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleTabChange('alerts')}
              >
                <Bell size={20} />
                <span>Alert Configuration</span>
              </button>
            </nav>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium mb-3">Care Team</h3>
            <div className="space-y-3">
              {caregivers.map(caregiver => (
                <div key={caregiver.id} className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-medium">
                    {caregiver.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{caregiver.name}</div>
                    <div className="text-xs text-gray-500">{caregiver.role}</div>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    new Date().getTime() - caregiver.lastAccess.getTime() < 3600000 * 12 
                      ? 'bg-green-500' 
                      : 'bg-gray-300'
                  }`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white rounded-lg shadow overflow-hidden">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default CaregiverPortal; 